<base-site-layout [heading]="'Lieferschein verknüpfen' | translate" [isDialog]="true" (dialogCloseClicked)="cancel()">
<div class="dialog-content">
  <!-- Supplier missing message -->
  @if (supplierMissing()) {
    <div class="alert alert-info mb-2">
      {{ 'Bitte zuerst einen Lieferanten auswählen, bevor Lieferscheine verknüpft werden können.' | translate }}
    </div>
  }

  <!-- Already linked receipts section with label -->
  @if (store.linkedSummaries()?.length && store.isInReceiptSearch()) {
    <div class="mb-3">
      <div class="text-muted mb-2 fw-semibold">{{ 'Bereits verknüpfte Lieferscheine' | translate }}</div>
      <div class="list-group">
        @for (rel of store.linkedSummaries(); track rel) {
          <button
            type="button"
            class="list-group-item list-group-item-action already-linked"
            (click)="selectReceiptFromSummary(rel)"
            >
            <div class="row align-items-center">
              <div class="col-auto">
              <div class="status-container text-center">
                <span class="badge" [ngClass]="rel.isOpen ? 'bg-success' : 'bg-secondary'">
                  @if (rel.isOpen) {
                    {{ 'offen' | translate }}
                  } @else {
                    {{ 'geschlossen' | translate }}
                  }
                </span>
              </div>
              @if (rel.inApproval) {
                <div class="status-container text-center">
                  <span class="badge bg-warning">{{ 'in Freigabe' | translate }}</span>
                </div>
              }
              <div class="status-container text-center">
                <span class="badge bg-primary">{{ 'bereits verknüpft' | translate }}</span>
              </div>
              </div>
              <div class="col">
                <div class="fw-bold">{{ rel.receiptNumber }}</div>
                <div class="text-muted">{{ rel.supplierDisplayName }}</div>
              </div>
              <div class="col-auto text-muted"></div>
            </div>
          </button>
        }
      </div>
    </div>
  }

  <!-- Stage 1: search receipts -->
  @if (store.isInReceiptSearch()) {
    <div class="col">
      <div class="row justify-content-end align-items-center">
        <div class="col-md col-sm-12">
          <div class="input-group mb-2">
            <span class="input-group-text"><em class="fas fa-search"></em></span>
            <input
              type="text"
              class="form-control"
              [placeholder]="'Suche nach Nr., Lieferant, Stichwort' | translate"
              [ngModel]="store.search()"
              (ngModelChange)="store.setSearch($event)"
              [disabled]="supplierMissing()"
              />
          </div>
        </div>
        <div class="col-auto mb-3">
          <div class="form-check">
            <mat-checkbox [formControl]="includeClosedControl">
              {{ 'zeige geschlossene' | translate }}
            </mat-checkbox>
          </div>
        </div>
      </div>
    </div>
    @if (!store.search()) {
      <div class="text-muted mb-2">
        {{
        'Es werden nur Lieferscheine des Lieferanten "\{\{supplierName\}\}" angezeigt. Über die Suche können Sie nach bestimmten Lieferscheinen filtern.'
        | translate: { supplierName: data.supplierDisplayName }
        }}
      </div>
    }

    @if (store.searching()) {
      <div class="py-2 text-center">
        <i class="fas fa-spinner fa-spin"></i>
      </div>
    }

    <!-- Search results section -->
    <ul class="list-group">
      <!-- Then render search results as usual -->
      @for (r of results(); track r) {
        <li
          class="list-group-item list-group-item-action scale--hover scale--lesser shadow--hover cursor-pointer"
          [class.already-linked]="linkedReceiptIds().has(r.id)"
          [class.disabled]="r.inApproval && !linkedReceiptIds().has(r.id)"
          (click)="selectReceipt(r)"
          >
          <div class="row align-items-center">
            <div class="col-auto">
              <div class="status-container text-center">
                <span class="badge" [ngClass]="r.isOpen ? 'bg-success' : 'bg-secondary'">
                  @if (r.isOpen) {
                    {{ 'offen' | translate }}
                  } @else {
                    {{ 'geschlossen' | translate }}
                  }
                </span>
              </div>
              @if (r.inApproval) {
                <div class="status-container text-center">
                  <span class="badge bg-warning ms-1">{{ 'in Freigabe' | translate }}</span>
                </div>
              }
              <div class="status-container text-center">
                @if (linkedReceiptIds().has(r.id)) {
                  <span class="badge bg-primary ms-1">
                    {{ 'verknüpft' | translate }}
                  </span>
                }
              </div>
            </div>
            <div class="col">
              <div class="fw-bold">{{ r.receiptNumber }}</div>
              <div class="text-muted">{{ r.supplierDisplayName }}</div>
            </div>
            <div class="col-auto text-muted">{{ r.documentDate | date: 'shortDate' }}</div>
          </div>
        </li>
      }
    </ul>
  }

  <!-- Stage 2: pick item from selected receipt -->
  @if (store.isInItemSelection()) {
    <!-- Selected receipt display with label -->
    <div class="mb-3">
      <div class="text-muted mb-2 fw-semibold">{{ 'Ausgewählter Lieferschein' | translate }}</div>
      <ul class="list-group">
        <li class="list-group-item already-linked">
          <div class="row align-items-center">
            <div class="col-auto">
              <span
                class="badge"
                [ngClass]="store.selectedReceiptStatus() ? 'bg-success' : 'bg-secondary'"
                >
                @if (store.selectedReceiptStatus()) {
                  {{ 'offen' | translate }}
                } @else {
                  {{ 'geschlossen' | translate }}
                }
              </span>
            </div>
            <div class="col">
              <div class="fw-bold">{{ selectedReceiptNumber() }}</div>
              @if (store.selectedReceipt()?.supplierDisplayName) {
                <div class="text-muted">{{ store.selectedReceipt()?.supplierDisplayName }}</div>
              }
            </div>
            @if (store.selectedReceiptInApproval()) {
              <div class="col-auto">
                <span class="badge bg-warning">{{ 'in Freigabe' | translate }}</span>
              </div>
            }
            <div class="col-auto text-muted">
              @if (store.selectedReceipt()?.documentDate) {
                {{ store.selectedReceipt()?.documentDate | dayjs: 'L' }}
              }
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Item selection heading -->
    <div class="text-muted mb-2 fw-semibold">{{ 'Position aus Lieferschein auswählen' | translate }}</div>

    @if (loadingItems()) {
      <div class="py-2 text-center">
        <i class="fas fa-spinner fa-spin"></i>
      </div>
    }

    <ul class="list-group">
      @for (item of receiptItems(); track item) {
        <li
          class="list-group-item list-group-item-action"
          [ngClass]="{ 'hoverable cursor-pointer scale--hover scale--lesser': !isItemDisabled(item.id) }"
          [class.disabled]="isItemDisabled(item.id)"
          [style.opacity]="isItemDisabled(item.id) ? 0.6 : 1"
          (click)="pickItem(item)"
          >
          <div class="row align-items-center">
            <div class="col-auto">
              <mat-radio-button
                color="primary"
                name="selItem"
                [checked]="selectedItemId() === item.id"
                [disabled]="isItemDisabled(item.id)"
              ></mat-radio-button>
            </div>
            <div class="col">
              <div class="fw-bold">
                {{ item.name }}
                @if (isItemLinkedToInvoice(item.id)) {
                  <span class="badge bg-primary ms-2">{{ 'bereits verknüpft' | translate }}</span>
                }
              </div>
              <div class="text-muted">
                @if (!expanded[item.id]) {
                  {{ item.note || '' | slice: 0 : 200 }}
                  @if ((item.note || '').length > 200) {
                    ...
                    <span
                      class="hoverable text-primary"
                      (click)="$event.preventDefault(); toggleExpand(item.id)"
                      >
                      {{ 'mehr anzeigen' | translate }}
                    </span>
                  }
                }
                @if (expanded[item.id]) {
                  {{ item.note }}
                  <a (click)="$event.preventDefault(); toggleExpand(item.id)">
                    {{ 'weniger anzeigen' | translate }}
                  </a>
                }
              </div>
              <div class="small text-muted">
                {{ 'Artikelnummer' | translate }}: {{ item.articleNumberSupplier }} |
                {{ 'Menge' | translate }}: {{ item.quantity }}
              </div>
            </div>
          </div>
        </li>
      }
    </ul>
  }
</div>

<div class="dialog-footer d-flex justify-content-between gap-2 mt-3">
  <div>
    @if (store.isInItemSelection()) {
      <button class="btn btn-outline-secondary" (click)="backToReceipts()">
        {{ 'zur Lieferschein Auswahl' | translate }}
      </button>
    }
  </div>
  <div class="d-flex gap-2">
    <button class="btn btn-secondary" (click)="cancel()">{{ 'Abbrechen' | translate }}</button>
    <button class="btn btn-primary" [disabled]="!(selectedReceiptId() && selectedItemId())" (click)="confirm()">
      {{ 'Verknüpfen' | translate }}
    </button>
  </div>
</div>
</base-site-layout>
